import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CONTACT_INFO, SERVICE_OPTIONS } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { firstName: "", lastName: "", email: "", phone: "", company: "", service: "", message: "" };

export default function Contact() {
  useSEO({
    title: "Contact | Tangensis Business Solutions",
    description: "Have a business challenge, ambitious idea or opportunity to explore? Get in touch with Tangensis Business Solutions — let's build what's next.",
    path: "/contact",
  });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm(initial);
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-brand-ink/5 border border-brand-ink/15 rounded-xl px-4 py-3.5 text-brand-ink placeholder-brand-ink/40 focus:border-brand-primary focus:bg-brand-ink/[0.08] transition-colors duration-300 outline-none";
  const labelCls = "block text-sm font-semibold text-brand-ink/70 mb-2";

  return (
    <>
      <PageHero
        label="Get in touch"
        titleLines={["LET'S BUILD", "WHAT'S NEXT."]}
        intro="Have a business challenge, ambitious idea or opportunity you'd like to explore? Let's talk."
      />

      <section className="bg-white pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="bg-brand-ink/[0.03] border border-brand-ink/10 rounded-3xl p-6 md:p-10">
                {sent && (
                  <div data-testid="contact-success" className="mb-6 flex items-center gap-3 rounded-xl border border-brand-primary/30 bg-brand-primary/[0.07] px-4 py-3.5 text-brand-ink">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-primary" />
                    <span className="font-semibold">Thanks — your message has been sent. We&apos;ll be in touch shortly.</span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className={labelCls}>First Name *</label>
                    <input id="firstName" data-testid="input-firstName" className={inputCls} value={form.firstName} onChange={set("firstName")} placeholder="Jane" />
                  </div>
                  <div>
                    <label htmlFor="lastName" className={labelCls}>Last Name *</label>
                    <input id="lastName" data-testid="input-lastName" className={inputCls} value={form.lastName} onChange={set("lastName")} placeholder="Doe" />
                  </div>
                  <div>
                    <label htmlFor="email" className={labelCls}>Email *</label>
                    <input id="email" type="email" data-testid="input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@company.com" />
                  </div>
                  <div>
                    <label htmlFor="phone" className={labelCls}>Phone</label>
                    <input id="phone" data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 000 000 0000" />
                  </div>
                  <div>
                    <label htmlFor="company" className={labelCls}>Company</label>
                    <input id="company" data-testid="input-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name" />
                  </div>
                  <div>
                    <label htmlFor="service" className={labelCls}>Service Interested In</label>
                    <select id="service" data-testid="input-service" className={`${inputCls} appearance-none`} value={form.service} onChange={set("service")}>
                      <option value="" className="bg-white">Select a service</option>
                      {SERVICE_OPTIONS.map((s) => <option key={s} value={s} className="bg-white">{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="message" className={labelCls}>Message *</label>
                  <textarea id="message" rows={5} data-testid="input-message" className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your challenge or opportunity..." />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full gradient-bg text-white text-base font-bold px-8 py-4 shadow-xl shadow-brand-magenta/30 hover:shadow-brand-orange/40 transition-shadow duration-300 disabled:opacity-60 w-full sm:w-auto">
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> SENDING</> : <>SEND MESSAGE <ArrowUpRight className="h-5 w-5" /></>}
                </button>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="space-y-5">
                {[
                  { icon: Mail, label: "Email", value: CONTACT_INFO.email },
                  { icon: Phone, label: "Phone", value: CONTACT_INFO.phone },
                  { icon: MapPin, label: "Address", value: CONTACT_INFO.address },
                ].map((it) => (
                  <div key={it.label} className="flex items-start gap-4 bg-brand-ink/[0.03] border border-brand-ink/10 rounded-2xl p-5">
                    <span className="h-11 w-11 rounded-full gradient-bg flex items-center justify-center shrink-0"><it.icon className="h-5 w-5 text-white" /></span>
                    <div>
                      <p className="text-sm uppercase tracking-widest text-brand-ink/75">{it.label}</p>
                      <p className="mt-1 text-brand-ink text-lg break-words">{it.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-14">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-brand-ink/10 h-72 md:h-96" data-testid="contact-map">
              <iframe
                title="Tangensis office — Kompally, Hyderabad"
                src="https://www.google.com/maps?q=Shree%20Krishna%20Homes%2C%20Kompally%2C%20Secunderabad%2C%20Telangana%20500078&z=15&output=embed"
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
